import React, { useState, useEffect, useRef } from 'react';

const SPORTS = [
  {
    key: 'badminton',
    name: 'Badminton',
    live: true,
    icon: '🏸',
    accent: '#0D9488',
    accentLight: '#F0FDFA',
    tagline: 'The fastest racket sport in the world',
    body: 'BWF (Badminton World Federation) governs the sport globally. With shuttlecocks travelling at over 300 km/h, badminton demands explosive speed, razor-sharp reflexes, and tactical intelligence.',
    governing: 'BWF — Badminton World Federation',
    govUrl: 'https://bwfbadminton.com',
    scoring: [
      { label: 'Format', value: 'Best of 3 games' },
      { label: 'Points', value: '21 points per game (15-pt format also supported)' },
      { label: 'Deuce', value: 'At 20–20: play until 2-point lead' },
      { label: 'Golden Point', value: 'At 29–29: next point wins' },
      { label: 'System', value: 'Rally point — every rally scores a point' },
    ],
    disciplines: ['Men\'s Singles', 'Women\'s Singles', 'Men\'s Doubles', 'Women\'s Doubles', 'Mixed Doubles'],
    appSupport: ['Knockout draw', 'League / Round-Robin', '21-pt & 15-pt scoring', 'Best of 1 or Best of 3', 'Seeding & BYE handling', 'Parallel court scheduling'],
  },
  {
    key: 'tennis',
    name: 'Tennis',
    live: false,
    icon: '🎾',
    accent: '#16a34a',
    accentLight: '#f0fdf4',
    tagline: 'The grand slam of racket sports',
    body: 'The International Tennis Federation (ITF) governs tennis worldwide. Played on grass, clay, and hard courts, tennis is a sport of strategy, stamina, and elegance.',
    governing: 'ITF — International Tennis Federation',
    govUrl: 'https://www.itftennis.com',
    scoring: [
      { label: 'Format', value: 'Best of 3 or Best of 5 sets' },
      { label: 'Points', value: '0 – 15 – 30 – 40 – Game' },
      { label: 'Deuce', value: 'At 40–40: Ad-In / Ad-Out (or No-Ad tiebreak)' },
      { label: 'Set', value: '6 games to win, tiebreak at 6–6' },
      { label: 'System', value: 'Side-out scoring (serve matters)' },
    ],
    disciplines: ['Men\'s Singles', 'Women\'s Singles', 'Men\'s Doubles', 'Women\'s Doubles', 'Mixed Doubles'],
    appSupport: ['Knockout draw', 'League / Round-Robin', 'Set-based score entry', 'Seeding & BYE handling', 'Parallel court scheduling'],
  },
  {
    key: 'table_tennis',
    name: 'Table Tennis',
    live: false,
    icon: '🏓',
    accent: '#dc2626',
    accentLight: '#fef2f2',
    tagline: 'Lightning reflexes on a 2.74m table',
    body: 'The ITTF (International Table Tennis Federation) oversees the fastest indoor sport. Modern table tennis rewards aggressive attacking play with topspin loops and lightning-quick footwork.',
    governing: 'ITTF — International Table Tennis Federation',
    govUrl: 'https://www.ittf.com',
    scoring: [
      { label: 'Format', value: 'Best of 5 or Best of 7 games' },
      { label: 'Points', value: '11 points per game' },
      { label: 'Deuce', value: 'At 10–10: play until 2-point lead (no cap)' },
      { label: 'Service', value: 'Alternates every 2 points' },
      { label: 'System', value: 'Rally point scoring' },
    ],
    disciplines: ['Men\'s Singles', 'Women\'s Singles', 'Men\'s Doubles', 'Women\'s Doubles', 'Mixed Doubles'],
    appSupport: ['Knockout draw', 'League / Round-Robin', '11-pt rally scoring', 'Seeding & BYE handling', 'Parallel court scheduling'],
  },
  {
    key: 'squash',
    name: 'Squash',
    live: false,
    icon: '🟡',
    accent: '#d97706',
    accentLight: '#fffbeb',
    tagline: 'Intense rallies in an enclosed court',
    body: 'The WSF (World Squash Federation) governs the sport. Squash is played in a four-walled court, combining cardiovascular endurance with tactical shot-making. It has been a long-time Olympic candidate.',
    governing: 'WSF — World Squash Federation',
    govUrl: 'https://www.worldsquash.org',
    scoring: [
      { label: 'Format', value: 'Best of 5 games (PAR scoring)' },
      { label: 'Points', value: '11 points per game' },
      { label: 'Deuce', value: 'At 10–10: play until 2-point lead' },
      { label: 'Service', value: 'Only server scores (traditional) or rally point (PAR)' },
      { label: 'System', value: 'Point-A-Rally (PAR) — modern standard' },
    ],
    disciplines: ['Men\'s Singles', 'Women\'s Singles', 'Men\'s Doubles'],
    appSupport: ['Knockout draw', 'League / Round-Robin', '11-pt PAR scoring', 'Seeding & BYE handling', 'Parallel court scheduling'],
  },
  {
    key: 'pickleball',
    name: 'Pickleball',
    live: false,
    icon: '🥒',
    accent: '#0891b2',
    accentLight: '#ecfeff',
    tagline: 'The fastest-growing sport in the world',
    body: 'The IFP (International Federation of Pickleball) governs this fast-growing paddle sport. Combining elements of tennis, badminton, and ping-pong, pickleball is played on a badminton-sized court with a perforated plastic ball.',
    governing: 'IFP — International Federation of Pickleball',
    govUrl: 'https://www.ifpickleball.org',
    scoring: [
      { label: 'Format', value: 'Best of 3 games (or single game to 11)' },
      { label: 'Points', value: '11 points per game, win by 2' },
      { label: 'Cap', value: 'Hard cap at 21 (win by 1 if 20–20)' },
      { label: 'Service', value: 'Only serving team scores (side-out rule)' },
      { label: 'Non-volley', value: 'Kitchen zone — no volleying within 7 ft of net' },
    ],
    disciplines: ['Singles', 'Men\'s Doubles', 'Women\'s Doubles', 'Mixed Doubles'],
    appSupport: ['Knockout draw', 'League / Round-Robin', '11-pt scoring with 2-pt lead', 'Seeding & BYE handling', 'Parallel court scheduling'],
  },
  {
    key: 'padel',
    name: 'Padel',
    live: false,
    icon: '🎾',
    accent: '#0F766E',
    accentLight: '#F0FDFA',
    tagline: 'Tennis meets squash in an enclosed court',
    body: 'The FIP (Federación Internacional de Pádel) oversees the sport, which has exploded in popularity across Europe and Latin America. Padel uses the same scoring as tennis but is played in an enclosed glass-and-metal court.',
    governing: 'FIP — Federación Internacional de Pádel',
    govUrl: 'https://www.padelfip.com',
    scoring: [
      { label: 'Format', value: 'Best of 3 sets (same as tennis)' },
      { label: 'Points', value: '0 – 15 – 30 – 40 – Game' },
      { label: 'Deuce', value: 'Ad-In / Ad-Out at deuce' },
      { label: 'Walls', value: 'Ball can bounce off glass walls after first bounce' },
      { label: 'Service', value: 'Underhand serve below waist height' },
    ],
    disciplines: ['Men\'s Doubles', 'Women\'s Doubles', 'Mixed Doubles'],
    appSupport: ['Knockout draw', 'League / Round-Robin', 'Set-based score entry', 'Seeding & BYE handling', 'Parallel court scheduling'],
  },
];

const SportsPage = () => {
  const [active, setActive] = useState('badminton');
  const detailRef = useRef(null);
  const sport = SPORTS.find(s => s.key === active);

  const handleSelect = (key) => {
    setActive(key);
    setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  // Auto-cycle for visual interest when user hasn't interacted
  const [autoPlay, setAutoPlay] = useState(true);
  useEffect(() => {
    if (!autoPlay) return;
    const t = setInterval(() => {
      setActive(prev => {
        const idx = SPORTS.findIndex(s => s.key === prev);
        return SPORTS[(idx + 1) % SPORTS.length].key;
      });
    }, 3200);
    return () => clearInterval(t);
  }, [autoPlay]);

  return (
    <div style={{ paddingBottom: '4rem' }}>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #134E4A 0%, #0F766E 50%, #0D9488 100%)',
        padding: '3.5rem 1rem 4rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Background sport icons */}
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3rem', opacity: 0.06, fontSize: '7rem', pointerEvents: 'none', userSelect: 'none', flexWrap: 'nowrap', overflow: 'hidden' }}>
          {['🏸','🎾','🏓','🟡','🥒','🎾','🏸','🎾'].map((ic, i) => <span key={i}>{ic}</span>)}
        </div>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 999, padding: '4px 14px', fontSize: '0.75rem', color: '#99F6E4', marginBottom: '1rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
            6 Sports Supported
          </div>
          <h1 style={{ color: 'white', fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 800, margin: '0 0 0.75rem', lineHeight: 1.15 }}>
            Every Rally. Every Point.<br />Every Sport.
          </h1>
          <p style={{ color: '#99F6E4', fontSize: '1rem', maxWidth: 480, margin: '0 auto', lineHeight: 1.7 }}>
            From the net cord to the baseline — our platform supports the world's leading racket sports with sport-specific scoring, rules, and tournament formats.
          </p>
        </div>
      </div>

      {/* ── Sport selector cards ───────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: '-2rem auto 0', padding: '0 1rem', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
          {SPORTS.map(s => {
            const isActive = active === s.key;
            return (
              <button
                key={s.key}
                onClick={() => { setAutoPlay(false); handleSelect(s.key); }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: '0.4rem', padding: '1.1rem 0.5rem',
                  background: isActive ? s.accent : 'white',
                  border: isActive ? `2px solid ${s.accent}` : '2px solid #f0f0f0',
                  borderRadius: 14, cursor: 'pointer',
                  boxShadow: isActive ? `0 8px 24px ${s.accent}30` : '0 2px 8px rgba(0,0,0,0.06)',
                  transform: isActive ? 'translateY(-3px)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ fontSize: '2rem', lineHeight: 1 }}>{s.icon}</span>
                <span style={{ fontSize: '0.8rem', fontWeight: 700, color: isActive ? 'white' : '#374151' }}>
                  {s.name}
                </span>
                {s.live
                  ? <span style={{ fontSize: '0.55rem', padding: '1px 6px', background: isActive ? 'rgba(255,255,255,0.25)' : '#d1fae5', color: isActive ? 'white' : '#065f46', borderRadius: 999, fontWeight: 700, border: isActive ? '1px solid rgba(255,255,255,0.4)' : '1px solid #6ee7b7', letterSpacing: '0.05em' }}>LIVE</span>
                  : <span style={{ fontSize: '0.55rem', padding: '1px 6px', background: isActive ? 'rgba(255,255,255,0.12)' : '#f3f4f6', color: isActive ? 'rgba(255,255,255,0.7)' : '#9ca3af', borderRadius: 999, fontWeight: 600, border: isActive ? '1px solid rgba(255,255,255,0.2)' : '1px solid #e5e7eb' }}>Soon</span>
                }
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Sport detail panel ─────────────────────────────────────────────── */}
      <div
        ref={detailRef}
        style={{ maxWidth: 900, margin: '2rem auto 0', padding: '0 1rem' }}
      >
        <div style={{
          background: 'white', borderRadius: 16, overflow: 'hidden',
          border: `1px solid ${sport.accent}30`,
          boxShadow: `0 4px 24px ${sport.accent}12`,
        }}>
          {/* Coloured header strip */}
          <div style={{
            background: `linear-gradient(135deg, ${sport.accent} 0%, ${sport.accent}cc 100%)`,
            padding: '1.5rem 2rem',
            display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '3rem', lineHeight: 1 }}>{sport.icon}</span>
            <div>
              <h2 style={{ color: 'white', margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>{sport.name}</h2>
              <p style={{ color: 'rgba(255,255,255,0.8)', margin: '2px 0 0', fontSize: '0.88rem' }}>{sport.tagline}</p>
            </div>
            <a
              href={sport.govUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              style={{
                marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                padding: '6px 14px', background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: 999,
                color: 'white', fontSize: '0.75rem', fontWeight: 600,
                textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              {sport.governing}
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 10L10 2M10 2H4M10 2V8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </a>
          </div>

          <div style={{ padding: '1.5rem 2rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

            {/* About */}
            <div style={{ gridColumn: '1 / -1' }}>
              <p style={{ color: '#374151', fontSize: '0.92rem', lineHeight: 1.75, margin: 0 }}>
                {sport.body}
              </p>
            </div>

            {/* Scoring */}
            <div>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', fontWeight: 700 }}>
                Scoring System
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {sport.scoring.map(row => (
                  <div key={row.label} style={{ display: 'flex', gap: '0.75rem', fontSize: '0.85rem' }}>
                    <span style={{ minWidth: 80, color: '#9ca3af', fontWeight: 600, flexShrink: 0 }}>{row.label}</span>
                    <span style={{ color: '#374151' }}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Disciplines */}
            <div>
              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', fontWeight: 700 }}>
                Disciplines
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1.25rem' }}>
                {sport.disciplines.map(d => (
                  <span key={d} style={{ fontSize: '0.78rem', padding: '3px 10px', background: sport.accentLight, color: sport.accent, borderRadius: 999, fontWeight: 600, border: `1px solid ${sport.accent}30` }}>
                    {d}
                  </span>
                ))}
              </div>

              <h4 style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', fontWeight: 700 }}>
                Supported in This App
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {sport.appSupport.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.82rem', color: '#374151' }}>
                    <span style={{ width: 16, height: 16, borderRadius: '50%', background: sport.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg width="8" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── All sports strip ───────────────────────────────────────────────── */}
      <div style={{ maxWidth: 900, margin: '2.5rem auto 0', padding: '0 1rem' }}>
        <h3 style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9ca3af', fontWeight: 700, textAlign: 'center', marginBottom: '1.25rem' }}>
          Quick Overview
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {SPORTS.map(s => (
            <button
              key={s.key}
              onClick={() => { setAutoPlay(false); handleSelect(s.key); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '1rem',
                padding: '0.75rem 1rem', background: 'white',
                border: active === s.key ? `1.5px solid ${s.accent}` : '1.5px solid #f3f4f6',
                borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                transition: 'border-color 0.15s',
              }}
            >
              <span style={{ fontSize: '1.4rem', width: 32, textAlign: 'center', flexShrink: 0 }}>{s.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.88rem', color: active === s.key ? s.accent : '#111827' }}>{s.name}</div>
                <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 1 }}>{s.tagline}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem', flexShrink: 0 }}>
                {s.live
                  ? <span style={{ fontSize: '0.55rem', padding: '1px 6px', background: '#d1fae5', color: '#065f46', borderRadius: 999, fontWeight: 700, border: '1px solid #6ee7b7', letterSpacing: '0.05em' }}>LIVE</span>
                  : <span style={{ fontSize: '0.55rem', padding: '1px 6px', background: '#f3f4f6', color: '#9ca3af', borderRadius: 999, fontWeight: 600, border: '1px solid #e5e7eb' }}>Coming Soon</span>
                }
                <span style={{ fontSize: '0.7rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                  {s.disciplines.length} discipline{s.disciplines.length !== 1 ? 's' : ''}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

export default SportsPage;
